import React from 'react';
import {
  Activity, CircleAlert, TriangleAlert, ArrowDownRight, ArrowRight, ArrowUpRight,
  ChartNoAxesColumn, Bell, Calendar, Check, ChevronLeft, ChevronRight, Code, CreditCard,
  Download, FileText, Globe, CircleHelp, Info, Layers, LifeBuoy, Lock, LogOut, Mail,
  MapPin, Minus, Ellipsis, Plus, RefreshCw, Search, Settings, Star, Users, Wrench, X,
  Zap
} from 'lucide-react';
import { iconPreviewNodes } from './iconPreviewNodes.js';

/* Lucide via lucide-react, 1.75px stroke, is the system's only icon set. Every icon is a
   named import, so the bundler tree-shakes lucide-react down to exactly these 36. A
   name lookup over the whole package would keep all ~2,000. No CDN, no window global.

   Names are the kebab-case names the system has always used. Canonical Lucide names for
   renamed icons (triangle-alert, circle-alert, circle-help, ellipsis, chart-no-axes-column)
   resolve too. An unknown name warns once and renders an empty, correctly sized span so
   layout never shifts. Adding an icon is deliberate: import it, add one entry below, and add
   its path data to iconPreviewNodes.js.

   The design tool's in-browser preview cannot resolve npm packages, so there, and only
   there, the imports are undefined and the glyph is drawn from iconPreviewNodes. */
const ALIASES = {
  'triangle-alert': 'alert-triangle', 'circle-alert': 'alert-circle', 'circle-help': 'help-circle',
  'circle-question-mark': 'help-circle', 'ellipsis': 'more-horizontal', 'chart-no-axes-column': 'bar-chart-2'
};
const ICONS = typeof Activity === 'undefined' || !Activity ? null : {
  "activity": Activity,
  "alert-circle": CircleAlert,
  "alert-triangle": TriangleAlert,
  "arrow-down-right": ArrowDownRight,
  "arrow-right": ArrowRight,
  "arrow-up-right": ArrowUpRight,
  "bar-chart-2": ChartNoAxesColumn,
  "bell": Bell,
  "calendar": Calendar,
  "check": Check,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "code": Code,
  "credit-card": CreditCard,
  "download": Download,
  "file-text": FileText,
  "globe": Globe,
  "help-circle": CircleHelp,
  "info": Info,
  "layers": Layers,
  "life-buoy": LifeBuoy,
  "lock": Lock,
  "log-out": LogOut,
  "mail": Mail,
  "map-pin": MapPin,
  "minus": Minus,
  "more-horizontal": Ellipsis,
  "plus": Plus,
  "refresh-cw": RefreshCw,
  "search": Search,
  "settings": Settings,
  "star": Star,
  "users": Users,
  "wrench": Wrench,
  "x": X,
  "zap": Zap
};
const warned = new Set();
const warn = (name) => {
  if (warned.has(name)) return;
  warned.add(name);
  console.warn('[Spruce Icon] "' + name + '" is not in the Spruce icon map (components/core/Icon.jsx)');
};

function PreviewGlyph({ nodes, size, strokeWidth }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {nodes.map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }))}
    </svg>
  );
}

export function Icon({ name, size = 16, strokeWidth = 1.75, className = '', style, ...rest }) {
  const key = ALIASES[name] || name;
  let glyph = null;
  if (ICONS) {
    const Glyph = ICONS[key];
    if (Glyph) glyph = <Glyph size={size} strokeWidth={strokeWidth} />;
  } else if (iconPreviewNodes[key]) {
    glyph = <PreviewGlyph nodes={iconPreviewNodes[key]} size={size} strokeWidth={strokeWidth} />;
  }
  if (!glyph && name) warn(name);
  return (
    <span
      className={'sp-icon ' + className}
      aria-hidden="true"
      style={{ width: size, height: size, ...style }}
      {...rest}
    >
      {glyph}
    </span>
  );
}
